/* LoginAdmin — pantalla ÚNICA de entrada al sistema DMU 614.
   - Tema oscuro institucional #0a1628 con acentos dorados #C9A84C.
   - Una sola pantalla: usuario + contraseña → ENTRAR.
   - El sistema detecta el rol y redirige automáticamente al módulo correspondiente
     (ver mapa en /app/frontend/src/auth/destinos.js).
   - Link discreto al pie para choferes (los choferes ahora operan vía WhatsApp Bot;
     este link queda como compatibilidad).
   Refactor 20-jun-2026 (Octavio): eliminada la pantalla intermedia de selección
   de módulo y lista de personas. */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { destinoParaRol } from "../auth/destinos";
import LogoMontanas from "../components/LogoMontanas";

const GOLD = "#C9A84C";
const NAVY = "#0a1628";

function formatErr(detail) {
  if (!detail) return "Algo salió mal.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" · ");
  return JSON.stringify(detail);
}

export default function LoginAdmin() {
  const navigate = useNavigate();
  const { loginWeb } = useAuth();
  const [usuario, setUsuario] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await loginWeb(usuario.trim(), password);
      const destino = destinoParaRol(u?.rol);
      navigate(destino, { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        setError(err?.response?.data?.detail || "Demasiados intentos. Espera unos minutos.");
      } else if (status === 401 || status === 400) {
        setError("Usuario o contraseña incorrectos");
      } else {
        setError(formatErr(err?.response?.data?.detail) || err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-login-admin">
      <div className="dmu-login-bg">
        <div className="dmu-login-card" data-testid="login-card">
          <header className="dmu-login-header">
            <LogoMontanas size={52} className="dmu-login-logo-svg" />
            <div>
              <div className="dmu-login-eyebrow">DMU 614</div>
              <h1 className="dmu-login-title">Control Operativo</h1>
            </div>
          </header>

          <form onSubmit={submit} className="space-y-4" data-testid="form-login" autoComplete="on">
            <div className="dmu-section-label">Acceso al sistema</div>

            <div className="dmu-field">
              <label htmlFor="usuario">Usuario</label>
              <input
                id="usuario"
                type="text"
                autoComplete="username"
                autoFocus
                value={usuario}
                onChange={(e) => setUsuario(e.target.value)}
                placeholder="Tu nombre de usuario"
                required
                data-testid="input-usuario"
              />
            </div>

            <div className="dmu-field">
              <label htmlFor="password">Contraseña</label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="input-password"
              />
            </div>

            {error && (
              <div className="dmu-error-msg" data-testid="error-msg">
                <i className="fa-solid fa-circle-exclamation mr-1" />
                {error}
              </div>
            )}

            <button
              type="submit"
              className="dmu-btn-primary"
              disabled={loading}
              data-testid="btn-submit"
            >
              {loading ? "Entrando…" : "ENTRAR"}
              {!loading && <i className="fa-solid fa-arrow-right" />}
            </button>
          </form>

            <div className="dmu-soporte">¿Problemas para entrar? · Contacta a Octavio</div>
          </div>
        </div>
        <StyleTagGlobal />
      </div>
  );
}

/* ───── Estilos globales del login institucional ───── */
function StyleTagGlobal() {
  return (
    <style>{`
      .dmu-login-bg {
        min-height: 100vh;
        background:
          radial-gradient(1100px 600px at 15% -10%, rgba(201,168,76,0.10), transparent 60%),
          radial-gradient(800px 500px at 100% 100%, rgba(30,64,128,0.35), transparent 60%),
          ${NAVY};
        display: flex; align-items: center; justify-content: center;
        padding: 1rem;
      }
      .dmu-login-card {
        width: 100%; max-width: 440px;
        background: rgba(255,255,255,0.03);
        border: 1px solid ${GOLD}33;
        border-radius: 20px;
        padding: 28px 22px;
        box-shadow: 0 30px 70px -20px rgba(0,0,0,0.6);
        backdrop-filter: blur(8px);
      }
      .dmu-login-header {
        display: flex; align-items: center; gap: 14px;
        margin-bottom: 18px; padding-bottom: 16px;
        border-bottom: 1px solid ${GOLD}22;
      }
      .dmu-login-logo-svg {
        width: 52px; height: 52px; min-width: 52px;
        border-radius: 10px;
        box-shadow: 0 4px 14px rgba(0,0,0,0.45);
        display: block;
      }
      .dmu-login-eyebrow {
        color: ${GOLD}; font-size: 10px; font-weight: 800;
        letter-spacing: 0.18em; text-transform: uppercase;
      }
      .dmu-login-title {
        color: #fff; font-size: 22px; font-weight: 800;
        margin: 2px 0; letter-spacing: -0.01em;
      }
      .dmu-login-sub {
        color: rgba(255,255,255,0.65); font-size: 12px;
      }
      .dmu-section-label {
        color: ${GOLD}; font-size: 11px; font-weight: 700;
        letter-spacing: 0.12em; text-transform: uppercase;
        margin: 4px 0 12px;
      }
      .dmu-field { display: flex; flex-direction: column; gap: 6px; margin-bottom: 12px; }
      .dmu-field label {
        color: rgba(255,255,255,0.85); font-size: 12px;
        font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em;
      }
      .dmu-field input {
        width: 100%;
        background: rgba(255,255,255,0.08);
        border: 1px solid ${GOLD}33;
        color: #fff;
        border-radius: 10px;
        padding: 12px 14px; font-size: 16px;
        min-height: 48px;
      }
      .dmu-field input:focus {
        outline: none; border-color: ${GOLD};
        box-shadow: 0 0 0 3px ${GOLD}22;
      }
      .dmu-error-msg {
        background: rgba(185,28,28,0.15);
        border: 1px solid rgba(248,113,113,0.35);
        color: #FCA5A5; font-size: 13px;
        padding: 10px 12px; border-radius: 8px;
        font-weight: 600;
      }
      .dmu-btn-primary {
        width: 100%;
        background: linear-gradient(135deg, ${GOLD}, #b8932f);
        color: ${NAVY};
        font-weight: 800; font-size: 14px;
        letter-spacing: 0.06em;
        border: 0; border-radius: 12px;
        padding: 14px; min-height: 52px;
        cursor: pointer;
        display: inline-flex; align-items: center; justify-content: center; gap: 10px;
        box-shadow: 0 8px 20px -6px rgba(201,168,76,0.4);
        margin-top: 8px;
      }
      .dmu-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
      .dmu-login-footer {
        margin-top: 18px; padding-top: 14px;
        border-top: 1px solid ${GOLD}22;
        text-align: center;
      }
      .dmu-link-discreto {
        background: transparent; border: 0;
        color: ${GOLD}; opacity: 0.85;
        font-size: 12px; font-weight: 600;
        cursor: pointer; text-decoration: none;
        padding: 6px 10px;
      }
      .dmu-link-discreto:hover { opacity: 1; text-decoration: underline; }
      .dmu-soporte {
        color: rgba(255,255,255,0.5);
        font-size: 11px; margin-top: 6px;
      }
    `}</style>
  );
}
